// Keyboard-wedge barcode scanner support (USB / Bluetooth HID scanners).
//
// Hardware scanners "type" the barcode characters extremely fast (~10-30ms
// between keys) and terminate with Enter. This hook attaches a window-level
// keydown listener that detects such bursts WITHOUT any input being focused:
// printable characters arriving with an inter-key gap below `maxGapMs` are
// buffered; an Enter arriving hot on a buffer of `minLength`+ characters fires
// `onScan(buffer)`. A gap above the threshold resets the buffer, so slow human
// typing can never accumulate into a phantom scan.
//
// When focus is inside a real text input (phone, discount, salesperson, manual
// barcode field), the hook deliberately does NOT steal keystrokes — the scanner
// burst lands in that field like normal typing, and fields that act on Enter
// (e.g. the manual barcode field's lookup) handle it themselves.

import { useEffect, useRef } from 'react';

export interface UseBarcodeScannerOptions {
  onScan: (code: string) => void;
  /** Minimum buffered length for an Enter to count as a scan. Default 4. */
  minLength?: number;
  /** Maximum inter-key gap (ms) inside a scanner burst. Default 50. */
  maxGapMs?: number;
  /** Detach the listener entirely when false. Default true. */
  enabled?: boolean;
}

// True when the focused element is a place where the user (or the scanner
// acting as a keyboard) is legitimately typing text.
const isTextEntryTarget = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

// The character the scanner meant, read from the PHYSICAL key (e.code) rather
// than e.key. A scanner is a keyboard, so the phone's active layout applies to
// it: with the 注音 keyboard selected, NK3705… arrived as 「NKˇ˙ㄢㄓ…」 and
// matched nothing (2026-09-30). e.code is layout-independent. Falls back to
// e.key for keys outside this table (and for browsers without e.code).
const CODE_CHARS: Record<string, [string, string]> = {
  Minus: ['-', '_'], Equal: ['=', '+'], Period: ['.', '>'], Comma: [',', '<'],
  Slash: ['/', '?'], Semicolon: [';', ':'], Quote: ["'", '"'], BracketLeft: ['[', '{'],
  BracketRight: [']', '}'], Backslash: ['\\', '|'], Backquote: ['`', '~'], Space: [' ', ' '],
  NumpadDecimal: ['.', '.'], NumpadSubtract: ['-', '-'], NumpadAdd: ['+', '+'],
  NumpadMultiply: ['*', '*'], NumpadDivide: ['/', '/'],
};
const SHIFTED_DIGITS = ')!@#$%^&*(';

export function scannedChar(e: Pick<KeyboardEvent, 'code' | 'key' | 'shiftKey'> & { getModifierState?: (k: string) => boolean }): string | null {
  const code = e.code || '';
  const digit = /^(?:Digit|Numpad)(\d)$/.exec(code);
  if (digit) return code.startsWith('Digit') && e.shiftKey ? SHIFTED_DIGITS[Number(digit[1])] : digit[1];
  const letter = /^Key([A-Z])$/.exec(code);
  if (letter) {
    const caps = typeof e.getModifierState === 'function' && e.getModifierState('CapsLock');
    return e.shiftKey !== caps ? letter[1] : letter[1].toLowerCase();
  }
  if (CODE_CHARS[code]) return CODE_CHARS[code][e.shiftKey ? 1 : 0];
  return typeof e.key === 'string' && e.key.length === 1 ? e.key : null;
}

export function useBarcodeScanner({
  onScan,
  minLength = 4,
  maxGapMs = 50,
  enabled = true,
}: UseBarcodeScannerOptions): void {
  // Keep the latest callback without re-binding the listener every render.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Rule (b): never steal keystrokes from a focused text input.
      if (isTextEntryTarget(e.target)) return;

      const now = Date.now();

      if (e.key === 'Enter') {
        const code = bufferRef.current;
        bufferRef.current = '';
        // Accept only if the burst is long enough AND Enter arrived at
        // scanner speed after the last character (a human pressing Enter
        // later than the gap threshold clears, never fires).
        if (code.length >= minLength && now - lastKeyTimeRef.current <= maxGapMs) {
          e.preventDefault(); // don't "click" whatever button happens to be focused
          onScanRef.current(code);
        }
        return;
      }

      // Only printable single characters build the buffer; modifier chords
      // (Ctrl+C etc.) and navigation keys are ignored.
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const ch = scannedChar(e);
      if (ch == null) return;

      if (now - lastKeyTimeRef.current > maxGapMs) {
        // Rule (c): human-speed gap — restart the buffer at this character.
        bufferRef.current = ch;
      } else {
        bufferRef.current += ch;
      }
      lastKeyTimeRef.current = now;
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, minLength, maxGapMs]);
}
